import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './CreerCandidatureModal.css';
import Button from '../../../../composants/Button/Button';
import type { DemoZone, DemoArticle } from '../DemoInitModal';

// ── Types ────────────────────────────────────────────────────────────────────

interface Region {
  id: number;
  nom: string;
}

interface LotDepartementRef {
  id: number;
}

interface Departement {
  id: number;
  nom: string;
  id_region: number;
  region?: Region;
  lots_departements?: LotDepartementRef[];
}

interface RegionGroup {
  regionId: number;
  regionNom: string;
  departements: Departement[];
}

interface LotTechnique {
  id: number;
  nom: string;
  code: number;
}

interface DemoCreerCandidatureModalProps {
  accordCadreId: number;
  onClose: () => void;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function isRegional(code: number): boolean {
  return (code >= 100 && code < 200) || (code >= 300 && code < 400);
}

function isDepartemental(code: number): boolean {
  return code >= 200 && code < 300;
}

/** Retourne l'id du lot_departement d'un département, ou null si absent. */
function getLotDeptId(dept: Departement): number | null {
  return dept.lots_departements?.[0]?.id ?? null;
}

/**
 * Construit la liste DemoZone[] complète pour un lot à partir des départements de l'API.
 * - Lots régionaux (1xx, 3xx) : une zone par région unique
 * - Lots départementaux (2xx) : une zone par lot_departement
 */
function buildLotsDisponibles(lot: LotTechnique, departements: Departement[]): DemoZone[] {
  if (isRegional(lot.code)) {
    const regionMap = new Map<number, Region>();
    departements.forEach((dept) => {
      if (dept.region && !regionMap.has(dept.region.id)) {
        regionMap.set(dept.region.id, dept.region);
      }
    });
    return Array.from(regionMap.values())
      .sort((a, b) => a.nom.localeCompare(b.nom, 'fr'))
      .map((region) => ({
        id: region.id,
        region: true,
        departement: { id: region.id, nom: region.nom },
      }));
  }

  if (isDepartemental(lot.code)) {
    return departements
      .filter((dept) => (dept.lots_departements?.length ?? 0) > 0)
      .sort((a, b) => a.id - b.id)
      .flatMap((dept) =>
        (dept.lots_departements ?? []).map((ld) => ({
          id: ld.id,
          region: false,
          departement: {
            id: dept.id,
            nom: dept.nom,
            id_region: dept.id_region,
            region: dept.region,
          },
        }))
      );
  }

  return [];
}

// ── Composant ────────────────────────────────────────────────────────────────

const DemoCreerCandidatureModal: React.FC<DemoCreerCandidatureModalProps> = ({
  accordCadreId,
  onClose,
}) => {
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL ?? '';
  const token = localStorage.getItem('token') ?? '';
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
    [token]
  );

  // ── État ─────────────────────────────────────────────────────────────────
  const [lotsTechniques, setLotsTechniques] = useState<LotTechnique[]>([]);
  const [departements, setDepartements] = useState<Departement[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [lotTechniqueId, setLotTechniqueId] = useState<number | ''>('');
  // Pour les lots régionaux : regionIds ; pour les lots départementaux : lotDeptIds
  const [selectedZoneIds, setSelectedZoneIds] = useState<Set<number>>(new Set());

  // ── Chargement initial ────────────────────────────────────────────────────
  useEffect(() => {
    setLoadingData(true);
    fetch(
      `${apiUrl}/publications/ac27/lots-disponibles?accord_cadre_id=${accordCadreId}&phase=1`,
      { headers }
    )
      .then((r) => r.json())
      .then((data) => {
        const payload = data?.data ?? data;
        setLotsTechniques(Array.isArray(payload?.lots_techniques) ? payload.lots_techniques : []);
        setDepartements(Array.isArray(payload?.departements) ? payload.departements : []);
      })
      .catch(() => setError('Impossible de charger les lots disponibles. Veuillez réessayer.'))
      .finally(() => setLoadingData(false));
  }, [apiUrl, headers, accordCadreId]);

  // Réinitialise la sélection quand le lot technique change
  useEffect(() => {
    setSelectedZoneIds(new Set());
  }, [lotTechniqueId]);

  // ── Fermeture sur Escape ──────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  // ── Lot sélectionné ───────────────────────────────────────────────────────
  const selectedLot = useMemo(
    () => lotsTechniques.find((l) => l.id === lotTechniqueId) ?? null,
    [lotsTechniques, lotTechniqueId]
  );

  // ── Groupes régions (lots 1xx / 3xx) ─────────────────────────────────────
  const regionGroups = useMemo((): RegionGroup[] => {
    if (!selectedLot || !isRegional(selectedLot.code)) return [];
    const map = new Map<number, RegionGroup>();
    departements.forEach((dept) => {
      if (!dept.region) return;
      const rid = dept.region.id;
      if (!map.has(rid)) {
        map.set(rid, { regionId: rid, regionNom: dept.region.nom, departements: [] });
      }
      map.get(rid)!.departements.push(dept);
    });
    return Array.from(map.values()).sort((a, b) =>
      a.regionNom.localeCompare(b.regionNom, 'fr')
    );
  }, [selectedLot, departements]);

  // ── Départements pour lots 2xx ────────────────────────────────────────────
  const departementsList = useMemo((): Departement[] => {
    if (!selectedLot || !isDepartemental(selectedLot.code)) return [];
    return [...departements]
      .filter((d) => (d.lots_departements?.length ?? 0) > 0)
      .sort((a, b) => a.id - b.id);
  }, [selectedLot, departements]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const toggleZone = useCallback((id: number) => {
    setSelectedZoneIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const toggleRegion = useCallback(
    (group: RegionGroup) => {
      const regionId = group.regionId;
      setSelectedZoneIds((prev) => {
        const next = new Set(prev);
        next.has(regionId) ? next.delete(regionId) : next.add(regionId);
        return next;
      });
    },
    []
  );

  // ── Démarrer la simulation ────────────────────────────────────────────────
  const handleDemarrer = async () => {
    if (!selectedLot || selectedZoneIds.size === 0) return;

    setLoadingArticles(true);
    setError(null);

    try {
      const res = await fetch(
        `${apiUrl}/publications/ac27/lots-techniques/${selectedLot.id}/articles`,
        { headers }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const articles: DemoArticle[] = data.articles ?? [];

      const lotsDisponibles: DemoZone[] = buildLotsDisponibles(selectedLot, departements);

      navigate('/publications/ac27/demo', {
        state: {
          accordCadreId,
          lotTechnique: {
            id: selectedLot.id,
            code: String(selectedLot.code),
            nom: selectedLot.nom,
          },
          lotsDisponibles,
          articles,
          initialSelectedZoneIds: Array.from(selectedZoneIds),
        },
      });
    } catch {
      setError('Impossible de charger les articles du lot. Veuillez réessayer.');
    } finally {
      setLoadingArticles(false);
    }
  };

  const canDemarrer =
    !!lotTechniqueId && selectedZoneIds.size > 0 && !loadingData && !loadingArticles;

  // ── Rendu ─────────────────────────────────────────────────────────────────
  return (
    <div
      className="creer-candidature-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="creer-candidature-modal" role="dialog" aria-modal="true">

        {/* En-tête */}
        <div className="creer-candidature-modal-header">
          <div className="creer-candidature-header-left">
            <h2>Simulation de candidature</h2>
          </div>
          <button
            className="creer-candidature-modal-close"
            onClick={onClose}
            aria-label="Fermer"
          >
            ×
          </button>
        </div>

        {/* Corps */}
        <div className="creer-candidature-modal-body">
          <p className="creer-candidature-demo-notice">
            Mode démo — aucune donnée ne sera enregistrée.
          </p>

          {error && <div className="creer-candidature-error">{error}</div>}

          {loadingData ? (
            <div className="creer-candidature-loading">
              <span className="creer-candidature-spinner" />
              Chargement des données…
            </div>
          ) : (
            <>
              {/* Sélection du lot technique */}
              <div>
                <label className="creer-candidature-section-label">Lot technique</label>
                <select
                  className="creer-candidature-select"
                  value={lotTechniqueId}
                  onChange={(e) =>
                    setLotTechniqueId(e.target.value === '' ? '' : Number(e.target.value))
                  }
                >
                  <option value="">— Sélectionnez un lot —</option>
                  {lotsTechniques.map((lt) => (
                    <option key={lt.id} value={lt.id}>
                      Lot {lt.code} — {lt.nom}
                    </option>
                  ))}
                </select>
              </div>

              {/* Sélection des zones */}
              {selectedLot && (
                <div>
                  <label className="creer-candidature-section-label">
                    {isRegional(selectedLot.code) ? 'Régions couvertes' : 'Départements couverts'}
                  </label>

                  {/* Lots régionaux (1xx, 3xx) — une case par région */}
                  {isRegional(selectedLot.code) && (
                    <>
                      {regionGroups.length === 0 ? (
                        <p className="creer-candidature-empty">Aucune région disponible.</p>
                      ) : (
                        <div className="creer-candidature-lots-container">
                          {regionGroups.map((group) => (
                            <label
                              key={group.regionId}
                              className="creer-candidature-lot-item"
                            >
                              <input
                                type="checkbox"
                                checked={selectedZoneIds.has(group.regionId)}
                                onChange={() => toggleRegion(group)}
                              />
                              <span className="creer-candidature-lot-item-label">
                                {group.regionNom}
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </>
                  )}

                  {/* Lots départementaux (2xx) — une case par département */}
                  {isDepartemental(selectedLot.code) && (
                    <>
                      {departementsList.length === 0 ? (
                        <p className="creer-candidature-empty">Aucun département disponible.</p>
                      ) : (
                        <div className="creer-candidature-lots-container">
                          {departementsList.map((dept) => {
                            const lotId = getLotDeptId(dept);
                            if (lotId === null) return null;
                            return (
                              <label
                                key={dept.id}
                                className="creer-candidature-lot-item"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedZoneIds.has(lotId)}
                                  onChange={() => toggleZone(lotId)}
                                />
                                <span className="creer-candidature-lot-item-label">
                                  {String(dept.id).padStart(2, '0')} - {dept.nom}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}

                  {selectedZoneIds.size > 0 && (
                    <p className="creer-candidature-selection-count">
                      <strong>{selectedZoneIds.size}</strong>{' '}
                      {isRegional(selectedLot.code) ? 'région' : 'lot'}
                      {selectedZoneIds.size > 1 ? 's' : ''} sélectionné
                      {selectedZoneIds.size > 1 ? 's' : ''}
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="creer-candidature-modal-footer">
          <Button variant="secondary" value="Annuler" onClick={onClose} />
          <Button
            variant="primary"
            value={loadingArticles ? 'Chargement…' : 'Démarrer la simulation →'}
            onClick={handleDemarrer}
            disabled={!canDemarrer}
          />
        </div>
      </div>
    </div>
  );
};

export default DemoCreerCandidatureModal;
